import { useEffect, useState } from 'react';
import { FormikProps } from 'formik';
import { priceMask, REQUIRED_FIELD_ERROR2 } from '../../../utils/constants';
import { PriceFormType } from '../../../redux/types/pricesTypes';
import { InputType, OptionsListType } from '../../../utils/@globalTypes';
import { getCurrentYearMonthOptions } from '../../../utils/functions';
import Input from '../../Input';
import SelectComponentV2 from '../../SelectComponentV2';

import styles from './SettingFormInner.module.scss';
import RadioButton from '../../RadioButton';
import ObjectPriceSample from '../ObjectPriceSample';
import { useSelector } from 'react-redux';
import { RentalObjectSelectors } from '../../../redux/reducers/rentalObjectSlice';
import Checkbox from '../../Checkbox';
import ObjectKidsPrice from '../ObjectKidsPrice';
import { useTouchedValidation } from '../../../utils/useTouchedValidation';
import {
  clearBlockErrorsIfEmpty,
  clearConditionalErrors,
} from '../../../utils/clearBlockErrorsIfEmpty';

const MonthOptions: OptionsListType = [
  { value: '01', label: 'Январь' },
  { value: '02', label: 'Февраль' },
  { value: '03', label: 'Март' },
  { value: '04', label: 'Апрель' },
  { value: '05', label: 'Май' },
  { value: '06', label: 'Июнь' },
  { value: '07', label: 'Июль' },
  { value: '08', label: 'Август' },
  { value: '09', label: 'Сентябрь' },
  { value: '10', label: 'Октябрь' },
  { value: '11', label: 'Ноябрь' },
  { value: '12', label: 'Декабрь' },
];
const currentYearMonth = getCurrentYearMonthOptions();

type SettingFormProps = {
  formik: FormikProps<PriceFormType>;
  commonError: string;
  setCommonError: React.Dispatch<React.SetStateAction<string>>;
};
const SettingFormInner = ({
  formik,
  commonError,
  setCommonError,
}: SettingFormProps) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldTouched,
    setFieldValue,
    setFieldError,
    setErrors,
    setTouched    
  } = formik;

  useTouchedValidation();

  const typeOfPrice = ['Фиксированные', 'Гибкие'];

  const currentRentalObject = useSelector(
    RentalObjectSelectors.getCurrentRentalObject,
  );
  const max_places = currentRentalObject?.max_places ?? 100;

  const [activeRadioBtn, setActiveRadioBtn] = useState<number>(
    values.isPriceDynamic ? 1 : 0,
  );

  useEffect(() => {
    if (activeRadioBtn === 0) {
      setFieldValue('isPriceDynamic', false);
    }
    if (activeRadioBtn === 1) {
      setFieldValue('isPriceDynamic', true);
    }
  }, [activeRadioBtn]);

  useEffect(() => {
    if (
      values.isPriceDynamic === true &&
      touched.extra_guest_price?.high_season_weekday_price
    ) {
      if (
        !values.extra_guest_price?.high_season_weekday_price &&
        !errors.extra_guest_price?.high_season_weekday_price
      ) {
        setFieldError(
          'extra_guest_price.high_season_weekday_price',
          REQUIRED_FIELD_ERROR2,
        );
      }
      if (values.extra_guest_price?.high_season_weekday_price) {
        setFieldError(`extra_guest_price.high_season_weekday_price`, undefined);
      }
    }
  }, [
    values?.extra_guest_price,
    touched?.extra_guest_price,
    errors?.extra_guest_price,
  ]);

  useEffect(() => {
    if (
      values.isPriceDynamic === true &&
      touched.extra_guest_price?.high_season_weekend_price
    ) {
      if (
        !values.extra_guest_price?.high_season_weekend_price &&
        !errors.extra_guest_price?.high_season_weekend_price
      ) {
        setFieldError(
          'extra_guest_price.high_season_weekend_price',
          REQUIRED_FIELD_ERROR2,
        );
      }
      if (values.extra_guest_price?.high_season_weekend_price) {
        setFieldError(`extra_guest_price.high_season_weekend_price`, undefined);
      }
    }
  }, [
    values?.extra_guest_price,
    touched?.extra_guest_price,
    errors?.extra_guest_price,
  ]);

  useEffect(() => {
    if (
      values.isPriceDynamic === true &&
      touched.extra_guest_price?.low_season_weekday_price
    ) {
      if (
        !values.extra_guest_price.low_season_weekday_price &&
        !errors.extra_guest_price?.low_season_weekday_price
      ) {
        setFieldError(
          'extra_guest_price.low_season_weekday_price',
          REQUIRED_FIELD_ERROR2,
        );
      }
      if (values.extra_guest_price.low_season_weekday_price) {
        setFieldError(`extra_guest_price.low_season_weekday_price`, undefined);
      }
    }
  }, [
    values?.extra_guest_price,
    touched?.extra_guest_price,
    errors?.extra_guest_price,
  ]);

  useEffect(() => {
    if (
      values.isPriceDynamic === true &&
      touched.extra_guest_price?.low_season_weekend_price
    ) {
      if (
        !values.extra_guest_price.low_season_weekend_price &&
        !errors.extra_guest_price?.low_season_weekend_price
      ) {
        setFieldError(
          'extra_guest_price.low_season_weekend_price',
          REQUIRED_FIELD_ERROR2,
        );
      }
      if (values.extra_guest_price.low_season_weekend_price) {
        setFieldError(`low_season_weekend_price`, undefined);
      }
    }
  }, [
    values?.extra_guest_price,
    touched?.extra_guest_price,
    errors?.extra_guest_price,
  ]);

  useEffect(() => {
    if (
      (values.extra_guest_price.high_season_weekday_price &&
        values.extra_guest_price.high_season_weekend_price &&
        values.extra_guest_price.low_season_weekday_price &&
        values.extra_guest_price.low_season_weekend_price) ||
      !values.isPriceDynamic
    ) {
      clearBlockErrorsIfEmpty(errors, setErrors, 'extra_guest_price');
    }
  }, [values, touched?.extra_guest_price, errors?.extra_guest_price]);

  useEffect(() => {
    clearConditionalErrors(errors, touched, setErrors, setTouched, {
      isPriceDynamic: values.isPriceDynamic,
      has_kidsPrice: values.has_kidsPrice,
    });
  }, [values.isPriceDynamic, values.has_kidsPrice]);

  const helpText =
    activeRadioBtn === 0
      ? 'Фиксированные цены устанавливаются в целом за объект и не зависят от количества и состава гостей'
      : 'Гибкие цены зависят от количества и состава гостей';

  let minGuestOptions: OptionsListType = [];
  for (let i = 1; i <= max_places; i++) {
    minGuestOptions.push({ value: i.toString(), label: i.toString() });
  }

  useEffect(() => {
    if (
      values.start_date_month &&
      values.start_date_month === values.end_date_month &&
      values.start_date_day &&
      values.start_date_day === values.end_date_day
    ) {
      setCommonError('Даты начала и окончания не должны совпадать');
    } else setCommonError('');
  }, [values]);

  return (
    <>
      <h3 className={styles.contentTitle}>Настройки высокого сезона</h3>
      <div className={styles.dates}>
        <div>
          <p className={styles.label}>
            Дата начала высокого сезона* (дата заезда)
          </p>
          <div className={styles.date}>
            <SelectComponentV2
              id="start_date_month"
              title="Месяц"
              placeholder="—"
              optionsList={MonthOptions}
              currentValue={values.start_date_month}
              onBlur={() => setFieldTouched('start_date_month')}
              setSelecValue={(value) => {
                setFieldValue('start_date_month', value);
                setFieldValue('start_date_day', '');
              }}
              className={styles.month}
              errText={
                (touched.start_date_month && errors.start_date_month) ||
                (commonError && ' ')
              }
            />
            <SelectComponentV2
              id="start_date_day"
              title="День"
              placeholder="—"
              optionsList={currentYearMonth[values.start_date_month]}
              currentValue={values.start_date_day}
              setSelecValue={handleChange('start_date_day')}
              maxMenuHeight={200}
              onBlur={() => setFieldTouched('start_date_day')}
              className={styles.day}
              errText={
                (touched.start_date_day && errors.start_date_day) ||
                (commonError && ' ')
              }
              isDisabled={!values.start_date_month}
            />
          </div>
          {commonError && <p className={styles.errorText}>{commonError}</p>}
        </div>
        <div>
          <p className={styles.label}>
            Дата окончания высокого сезона* (дата заезда)
          </p>
          <div className={styles.date}>
            <SelectComponentV2
              id="end_date_month"
              title="Месяц"
              placeholder="—"
              optionsList={MonthOptions}
              currentValue={values.end_date_month}
              setSelecValue={(value) => {
                setFieldValue('end_date_month', value);
                setFieldValue('end_date_day', '');
              }}
              onBlur={() => setFieldTouched('end_date_month')}
              className={styles.month}
              errText={
                (touched.end_date_month && errors.end_date_month) ||
                (commonError && ' ')
              }
            />
            <SelectComponentV2
              id="end_date_day"
              title="День"
              placeholder="—"
              optionsList={currentYearMonth[values.end_date_month]}
              currentValue={values.end_date_day}
              setSelecValue={handleChange('end_date_day')}
              maxMenuHeight={200}
              onBlur={() => setFieldTouched('end_date_day')}
              className={styles.day}
              errText={
                (touched.end_date_day && errors.end_date_day) ||
                (commonError && ' ')
              }
              isDisabled={!values.end_date_month}
            />
          </div>
          {commonError && <p className={styles.errorText}>{commonError}</p>}
        </div>
      </div>
      <div>
        <h3 className={styles.contentTitle}>Тип формирования цен</h3>
        <div className={styles.setting}>
          <RadioButton
            activeTab={values.isPriceDynamic ? 1 : 0}
            btnsList={typeOfPrice}
            size="price"
            onClick={setActiveRadioBtn}
          />
          <div className={styles.setting_help}>{helpText}</div>
        </div>
        {errors.max_places && (
          <p className={styles.errorText}>{errors.max_places}</p>
        )}
      </div>

      <ObjectPriceSample
        title={
          activeRadioBtn === 1
            ? 'Базовые цены за проживание'
            : 'Цены за проживание'
        }
        itemName="base_price"
      />
      {activeRadioBtn === 1 && (
        <div className={styles.addBlock}>
          <ObjectPriceSample
            title="Дополнительный гость"
            itemName="extra_guest_price"
          />
          <div className={styles.lineTitle}>Минимальное количество гостей</div>
          <div className={styles.guests}>
            <div className={styles.count_guests}>
              <SelectComponentV2
                title="в будни"
                placeholder="-"
                // defaultValueId={0}
                optionsList={minGuestOptions}
                currentValue={values.extra_guest_price.min_guests_weekday}
                className={styles.guest}
                setSelecValue={handleChange(
                  'extra_guest_price.min_guests_weekday',
                )}
                errText={errors.extra_guest_price?.min_guests_weekday}
              />
            </div>
            <div className={styles.count_guests}>
              <SelectComponentV2
                title="в выходные"
                placeholder="-"
                // defaultValueId={0}
                optionsList={minGuestOptions}
                currentValue={values.extra_guest_price.min_guests_weekend}
                className={styles.guest}
                setSelecValue={handleChange(
                  'extra_guest_price.min_guests_weekend',
                )}
                errText={errors.extra_guest_price?.min_guests_weekend}
              />
            </div>
          </div>

          <Checkbox
            checked={values.has_kidsPrice}
            name="has_kidsPrice"
            label="Настройки цен для детей"
            onClick={() => {
              setFieldValue('has_kidsPrice', !values.has_kidsPrice);
            }}
          />

          {values.has_kidsPrice && <ObjectKidsPrice />}
        </div>
      )}
      <Checkbox
        checked={values.isAnimalPrice}
        name="isAnimalPrice"
        label="Платное размещение животных"
        onClick={() => {
          setFieldValue('isAnimalPrice', !values.isAnimalPrice);
        }}
      />
      {values.isAnimalPrice && (
        <div className={styles.animalWrap}>
          <Input
            id="animalPrice"
            name="animalPrice"
            value={values.animalPrice}
            title="Размещение 1 животного"
            placeholder="0.00"
            type={InputType.MASK}
            onChange={handleChange('animalPrice')}
            errText={errors.animalPrice}
            onBlur={() => setFieldTouched('animalPrice')}
            containerClassName="price"
            mask={priceMask}
          />
        </div>
      )}
    </>
  );
};

export default SettingFormInner;
